import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import { getFulfillmentTimeOrASAPDisplaySelector } from 'cart/selectors';
import { isMobileMedia as getIsMobileMedia } from 'utils/media';
import {
  getAvailableFulfillmentMethodSelector,
  areAllLocationsClosedSelector
} from 'locations/selectors';
import { goToCart } from 'cart/actions';
import { goToOrderSettings } from 'cart/actions/orderSettings';
import { setCartWorkingCopy } from 'cart/actions/cartWorkingCopy';

import { ENABLE_DELIVERY } from 'base/constants/flags';
import { LINK_TO_BENTO_WEBSITE } from 'base/constants/endpoints';
import {
  fulfillmentOptions,
  ALL_FULFILLMENT_METHODS
} from 'cart/constants/fulfillmentOptions';
import { LOCATION_BASE, LOCATION_OFFLINE } from 'locations/constants/paths';
import NavFulfillmentOptions from 'base/components/NavFulfillmentOptions';
import FeatureFlag from 'base/components/FeatureFlag';
import NavCartButton from 'base/components/NavCartButton';
import AppBar from 'base/components/AppBar';

import isDineInApplicationVariant from 'utils/isDineInApplicationVariant';

import NavLink from 'base/ui_components/NavLink';
import NavLinkLabel from 'base/ui_components/NavLinkLabel';
import NavBarDivider from 'base/ui_components/NavBarDivider';
import Box from '@material-ui/core/Box';
import NavFulfillmentIcon from 'base/ui_components/NavFulfillmentIcons';
import NavLinkWrapper from 'base/ui_components/NavLinkWrapper';

class AppBarContainer extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func,
    cartQuantity: PropTypes.number,
    accountName: PropTypes.string,
    logo: PropTypes.string,
    shouldDisplayCart: PropTypes.bool,
    shouldDisplayFulfillmentOptions: PropTypes.bool,
    fulfillmentTimeOrASAPDisplay: PropTypes.string,
    fulfillmentMethod: PropTypes.string,
    availableFulfillmentOptions: PropTypes.arrayOf(
      PropTypes.oneOf(ALL_FULFILLMENT_METHODS)
    ),
    deliveryAddress: PropTypes.string,
    isClosed: PropTypes.bool,
    logoDestinationUrl: PropTypes.string
  };

  goToCart = () => {
    this.props.dispatch(goToCart());
  };

  openModal = (selectedFulfillmentMethod = null) => {
    const { dispatch, fulfillmentMethod } = this.props;
    if (
      selectedFulfillmentMethod &&
      selectedFulfillmentMethod !== fulfillmentMethod
    ) {
      dispatch(
        setCartWorkingCopy({
          online_ordering_fulfillment_method: selectedFulfillmentMethod
        })
      );
    }
    dispatch(goToOrderSettings());
  };

  render() {
    const {
      accountName,
      logo,
      shouldDisplayCart,
      cartQuantity,
      fulfillmentTimeOrASAPDisplay,
      fulfillmentMethod,
      availableFulfillmentOptions,
      deliveryAddress,
      isClosed,
      shouldDisplayFulfillmentOptions,
      logoDestinationUrl
    } = this.props;
    const isMobileMedia = getIsMobileMedia();
    const isDineInVariant = isDineInApplicationVariant();

    return (
      <AppBar
        logo={logo}
        logoLinkPath={logoDestinationUrl || LINK_TO_BENTO_WEBSITE}
        name={accountName}
      >
        {/*
          -- WARNING --
          This is just a placeholder we need to figure out what the action
          should be when clicking on Dine-In
        */}
        {isDineInVariant && shouldDisplayFulfillmentOptions && (
          <Box m={'0 4px 0 0'}>
            <NavLinkWrapper>
              <NavLink highlighted>
                <NavLinkLabel bold uppercase dark highlighted>
                  Dine-In
                </NavLinkLabel>
              </NavLink>
              <Box
                display={{ xs: 'flex', md: 'none' }}
                p={'0 0 0 12px'}
                m={'0 -1px 0 0'}
              >
                <NavFulfillmentIcon icon={fulfillmentOptions.DINE_IN} />
              </Box>
            </NavLinkWrapper>
          </Box>
        )}

        {!isDineInVariant && shouldDisplayFulfillmentOptions && (
          <FeatureFlag
            flag={ENABLE_DELIVERY}
            renderComponent={
              <NavFulfillmentOptions
                deliveryAddress={deliveryAddress}
                openModal={this.openModal}
                shouldDisplayCart={shouldDisplayCart}
                fulfillmentMethod={fulfillmentMethod}
                fulfillmentTimeOrASAPDisplay={fulfillmentTimeOrASAPDisplay}
                availableFulfillmentOptions={availableFulfillmentOptions}
                isClosed={isClosed}
              />
            }
          />
        )}

        {shouldDisplayCart && !isMobileMedia && (
          <React.Fragment>
            {isDineInVariant && shouldDisplayFulfillmentOptions && (
              <NavBarDivider />
            )}
            <NavCartButton onClick={this.goToCart} itemsInCart={cartQuantity} />
          </React.Fragment>
        )}
      </AppBar>
    );
  }
}

const mapStateToProps = state => {
  const {
    account,
    cart,
    restaurantLocations,
    router,
    locationThrottledTimes
  } = state;
  const {
    quantity: cartQuantity,
    online_ordering_fulfillment_method: fulfillmentMethod,
    online_ordering_location: onlineOrderingLocation
  } = cart;

  const availableFulfillmentOptions = onlineOrderingLocation
    ? getAvailableFulfillmentMethodSelector({
        restaurantLocations,
        locationThrottledTimes,
        onlineOrderingLocation,
        cart
      })
    : [];

  const fulfillmentTimeOrASAPDisplay = getFulfillmentTimeOrASAPDisplaySelector({
    restaurantLocations,
    cart,
    locationThrottledTimes
  });
  const deliveryAddress =
    fulfillmentMethod === fulfillmentOptions.DELIVERY
      ? cart.shipping_street_address_1 || 'Your Address'
      : '';
  const isClosed = areAllLocationsClosedSelector(restaurantLocations);

  return {
    logo: get(
      account,
      'online_ordering_general_settings.logo[0].url',
      account.logo
    ),
    logoDestinationUrl: get(
      account,
      'online_ordering_general_settings.logo_destination_url'
    ),
    accountName: account.name,
    cartQuantity,
    shouldDisplayFulfillmentOptions: ![
      LOCATION_BASE,
      LOCATION_OFFLINE
    ].includes(router.location.pathname),
    shouldDisplayCart: cartQuantity > 0,
    fulfillmentTimeOrASAPDisplay,
    fulfillmentMethod,
    availableFulfillmentOptions,
    deliveryAddress,
    isClosed
  };
};

export default connect(mapStateToProps)(AppBarContainer);
